import type { ComboEntry } from "@innoclouds/db";
import { db } from "@innoclouds/db";
import { defineStore } from "pinia";
import { computed } from "vue";
import { useLiveQuery } from "#/composables/useLiveQuery";
import { isScheduleVisibleAt } from "#/utils/workSchedule";
import { useOrgUnitStore } from "./org-unit";
import { useScheduleStore } from "./schedule";

export const useHotmenuStore = defineStore("hotmenu", () => {
	const orgUnit = useOrgUnitStore();
	const scheduleStore = useScheduleStore();

	const { data: rawGroups } = useLiveQuery(
		(orgUnitId: string) => {
			if (!orgUnitId) return [];
			return db.groups.where("orgId").equals(orgUnitId).sortBy("position");
		},
		{ initialValue: [] },
		[() => orgUnit.id],
	);

	const { data: rawProducts } = useLiveQuery(
		(orgUnitId: string) => {
			if (!orgUnitId) return [];
			return db.products.where("orgId").equals(orgUnitId).sortBy("position");
		},
		{ initialValue: [] },
		[() => orgUnit.id],
	);

	const { data: rawCombos } = useLiveQuery(
		(orgUnitId: string) => {
			if (!orgUnitId) return [];
			return db.combos.where("orgId").equals(orgUnitId).sortBy("position");
		},
		{ initialValue: [] },
		[() => orgUnit.id],
	);

	let lastScheduled: ComboEntry[] = [];

	const scheduledCombos = computed(() => {
		const next = rawCombos.value?.filter((combo) => isScheduleVisibleAt(combo.schedule, scheduleStore.branchNow)) ?? [];
		const isSame = next.length === lastScheduled.length && next.every((combo, index) => combo === lastScheduled[index]);
		if (!isSame) lastScheduled = next;

		return lastScheduled;
	});

	const groups = computed(() => {
		const visible = scheduledCombos.value.length ? rawGroups.value : rawGroups.value?.filter((group) => !group.is_combo_group);
		return new Map((visible ?? []).map((group) => [group.id, group]));
	});
	const products = computed(() => new Map(rawProducts.value?.map((product) => [product.id, product])));
	const combos = computed(() => new Map(scheduledCombos.value.map((combo) => [combo.id, combo])));
	const allCombos = computed(() => new Map(rawCombos.value?.map((combo) => [combo.id, combo])));

	return {
		groups,
		products,
		combos,
		allCombos,
	};
});
